import React, { useState, useEffect } from 'react';
import { RiCloseLine, RiUploadLine, RiAddLine, RiDeleteBinLine } from 'react-icons/ri';
import Button from '@/components/common/Button/Button';
import Input from '@/components/forms/Input/Input';
import Spinner from '@/components/common/Spinner/Spinner';
import { type AdminProduct, type AdminBulkPricingTier } from '@/hooks/admin/useAdmin';
import { useCreateProduct, useUpdateProduct } from '@/hooks/admin/useAdminProducts';
import { useCategories } from '@/hooks/admin/useAdminCategories';
import { useMaterials } from '@/hooks/admin/useAdminMaterials';
import { useColors } from '@/hooks/admin/useAdminColors';
import { useSizes } from '@/hooks/admin/useAdminSizes';
import { useCustomizationOptions } from '@/hooks/admin/useAdminCustomizations';

interface ProductModalProps {
    isOpen: boolean;
    onClose: () => void;
    product?: AdminProduct;
    onSuccess?: () => void;
}

const slugify = (text: string): string => {
    return text
        .toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')           // Replace spaces with hyphens
        .replace(/[^\w\-]+/g, '')       // Remove special characters
        .replace(/\-\-+/g, '-')         // Replace multiple hyphens with one
        .replace(/^-+/, '')             // Remove hyphens from start
        .replace(/-+$/, '');            // Remove hyphens from end
};

const ProductModal: React.FC<ProductModalProps> = ({
    isOpen,
    onClose,
    product,
    onSuccess
}) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        short_description: '',
        category: 0,
        base_price: 0,
        main_image_url: '',
        gallery_images: [] as string[],
        weight: '',
        dimensions: '',
        is_active: true,
        is_featured: false,
        stock_quantity: 0,
        min_order_quantity: 1,
        allows_text_customization: false,
        allows_logo_upload: false,
        allows_pattern_upload: false,
        max_text_length: 100,
        available_materials: [] as number[],
        available_colors: [] as number[],
        available_sizes: [] as number[],
        available_customizations: [] as number[],
        meta_title: '',
        meta_description: ''
    });

    const [bulkPricing, setBulkPricing] = useState<AdminBulkPricingTier[]>([]);
    const [mainImageFile, setMainImageFile] = useState<File | null>(null);
    const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
    const [mainImagePreview, setMainImagePreview] = useState<string>('');

    const createProduct = useCreateProduct();
    const updateProduct = useUpdateProduct();

    const { data: categories = [] } = useCategories();
    const { data: materials = [] } = useMaterials();
    const { data: colors = [] } = useColors();
    const { data: sizes = [] } = useSizes();
    const { data: customizations = [] } = useCustomizationOptions();

    const isEditing = !!product;
    const isLoading = createProduct.isPending || updateProduct.isPending;

    useEffect(() => {
        if (product) {
            setFormData({
                name: product.name,
                description: product.description,
                short_description: product.short_description || '',
                category: product.category.id,
                base_price: product.base_price,
                main_image_url: product.main_image_url || '',
                gallery_images: product.gallery_images,
                weight: product.weight?.toString() || '',
                dimensions: product.dimensions || '',
                is_active: product.is_active,
                is_featured: product.is_featured,
                stock_quantity: product.stock_quantity,
                min_order_quantity: product.min_order_quantity,
                allows_text_customization: product.allows_text_customization,
                allows_logo_upload: product.allows_logo_upload,
                allows_pattern_upload: product.allows_pattern_upload,
                max_text_length: product.max_text_length,
                available_materials: product.available_materials,
                available_colors: product.available_colors,
                available_sizes: product.available_sizes,
                available_customizations: product.available_customizations,
                meta_title: product.meta_title || '',
                meta_description: product.meta_description || ''
            });
            setBulkPricing(product.bulk_pricing);
            setMainImagePreview(product.main_image_url || '');
        } else {
            setFormData({
                name: '',
                description: '',
                short_description: '',
                category: 0,
                base_price: 0,
                main_image_url: '',
                gallery_images: [],
                weight: '',
                dimensions: '',
                is_active: true,
                is_featured: false,
                stock_quantity: 0,
                min_order_quantity: 1,
                allows_text_customization: false,
                allows_logo_upload: false,
                allows_pattern_upload: false,
                max_text_length: 100,
                available_materials: [],
                available_colors: [],
                available_sizes: [],
                available_customizations: [],
                meta_title: '',
                meta_description: ''
            });
            setBulkPricing([]);
            setMainImagePreview('');
        }
        setMainImageFile(null);
        setGalleryFiles([]);
    }, [product, isOpen]);

    const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setMainImageFile(file);
            const reader = new FileReader();
            reader.onload = () => {
                setMainImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleGalleryImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        setGalleryFiles(prev => [...prev, ...files]);
    };

    const removeGalleryImage = (index: number) => {
        setGalleryFiles(prev => prev.filter((_, i) => i !== index));
    };

    const addBulkPricingTier = () => {
        setBulkPricing(prev => [...prev, {
            min_quantity: 0,
            max_quantity: undefined,
            discount_percentage: 0
        }]);
    };

    const updateBulkPricingTier = (index: number, field: keyof AdminBulkPricingTier, value: any) => {
        setBulkPricing(prev => prev.map((tier, i) =>
            i === index ? { ...tier, [field]: value } : tier
        ));
    };

    const removeBulkPricingTier = (index: number) => {
        setBulkPricing(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            alert('El nombre del producto es requerido');
            return;
        }

        if (!formData.category) {
            return;
        }

        if (formData.base_price < 0) {
            alert('El precio base debe ser mayor o igual a cero');
            return;
        }

        try {
            const selectedCategory = categories.find(cat => cat.id === formData.category)!;
            const mainImageUrl = mainImageFile ? 'uploaded-main-image-url' : formData.main_image_url;
            const galleryUrls = galleryFiles.length > 0 ? ['uploaded-gallery-url'] : formData.gallery_images;

            const productData = {
                ...formData,
                category: selectedCategory,
                main_image_url: mainImageUrl,
                gallery_images: galleryUrls,
                weight: formData.weight ? parseFloat(formData.weight) : undefined,
                slug: slugify(formData.name),     // Added slug generation
                bulk_pricing: bulkPricing // Changed from [] to bulkPricing
            };

            if (isEditing && product) {
                await updateProduct.mutateAsync({
                    id: product.id,
                    data: productData
                });
            } else {
                await createProduct.mutateAsync(productData);
            }

            onSuccess?.();
            onClose();
        } catch (error) {
            console.error('Error saving product:', error);
            alert('Error al guardar el producto');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="admin-modal-overlay">
            <div className="admin-modal-content admin-modal-large">
                <div className="admin-modal-header">
                    <h2 className="admin-modal-title">
                        {isEditing ? 'Editar Producto' : 'Nuevo Producto'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="admin-modal-close"
                        disabled={isLoading}
                    >
                        <RiCloseLine />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="admin-modal-form">
                    <div className="admin-form-sections">
                        {/* Basic Information */}
                        <div className="admin-form-section">
                            <h3 className="admin-section-title">Información Básica</h3>
                            <div className="admin-form-grid">
                                <div className="admin-form-group admin-form-full">
                                    <Input
                                        label="Nombre del Producto"
                                        name="name"
                                        value={formData.name}
                                        setValue={(value) => setFormData(prev => ({ ...prev, name: value }))}
                                        placeholder="Ingrese el nombre del producto"
                                        required
                                    />
                                </div>

                                <div className="admin-form-group">
                                    <label className="admin-form-label">Categoría</label>
                                    <select
                                        value={formData.category}
                                        onChange={(e) => setFormData(prev => ({ ...prev, category: parseInt(e.target.value) }))}
                                        className="admin-select"
                                        required
                                    >
                                        <option value={0}>Seleccionar Categoría</option>
                                        {categories.map(category => (
                                            <option key={category.id} value={category.id}>
                                                {category.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="admin-form-group">
                                    <Input
                                        label="Precio Base"
                                        name="base_price"
                                        type="number"
                                        step="0.01"
                                        value={formData.base_price.toString()}
                                        setValue={(value) => setFormData(prev => ({ ...prev, base_price: parseFloat(value) || 0 }))}
                                        placeholder="0.00"
                                        required
                                    />
                                </div>

                                <div className="admin-form-group">
                                    <Input
                                        label="Stock"
                                        name="stock_quantity"
                                        type="number"
                                        value={formData.stock_quantity.toString()}
                                        setValue={(value) => setFormData(prev => ({ ...prev, stock_quantity: parseInt(value) || 0 }))}
                                        placeholder="0"
                                    />
                                </div>

                                <div className="admin-form-group admin-form-full">
                                    <Input
                                        label="Descripción Corta"
                                        name="short_description"
                                        value={formData.short_description}
                                        setValue={(value) => setFormData(prev => ({ ...prev, short_description: value }))}
                                        placeholder="Descripción breve del producto"
                                        rows={2}
                                    />
                                </div>

                                <div className="admin-form-group admin-form-full">
                                    <Input
                                        label="Descripción Completa"
                                        name="description"
                                        value={formData.description}
                                        setValue={(value) => setFormData(prev => ({ ...prev, description: value }))}
                                        placeholder="Descripción detallada del producto"
                                        rows={4}
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Images */}
                        <div className="admin-form-section">
                            <h3 className="admin-section-title">Imágenes</h3>
                            <div className="admin-form-grid">
                                <div className="admin-form-group">
                                    <label className="admin-form-label">Imagen Principal</label>
                                    <div className="admin-image-upload">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleMainImageChange}
                                            className="admin-file-input"
                                            id="main-image"
                                        />
                                        <label htmlFor="main-image" className="admin-file-label">
                                            <RiUploadLine className="admin-upload-icon" />
                                            <span>Seleccionar Imagen Principal</span>
                                        </label>
                                        {mainImagePreview && (
                                            <div className="admin-image-preview">
                                                <img src={mainImagePreview} alt="Main preview" />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="admin-form-group admin-form-full">
                                    <label className="admin-form-label">Galería de Imágenes</label>
                                    <div className="admin-gallery-upload">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            onChange={handleGalleryImageChange}
                                            className="admin-file-input"
                                            id="gallery-images"
                                        />
                                        <label htmlFor="gallery-images" className="admin-file-label">
                                            <RiUploadLine className="admin-upload-icon" />
                                            <span>Agregar Imágenes a la Galería</span>
                                        </label>
                                        <div className="admin-gallery-preview">
                                            {galleryFiles.map((file, index) => (
                                                <div key={index} className="admin-gallery-item">
                                                    <img src={URL.createObjectURL(file)} alt={`Gallery ${index}`} />
                                                    <button
                                                        type="button"
                                                        onClick={() => removeGalleryImage(index)}
                                                        className="admin-gallery-remove"
                                                    >
                                                        <RiDeleteBinLine />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Customization Options */}
                        <div className="admin-form-section">
                            <h3 className="admin-section-title">Opciones de Personalización</h3>
                            <div className="admin-form-grid">
                                <div className="admin-form-group">
                                    <label className="admin-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={formData.allows_text_customization}
                                            onChange={(e) => setFormData(prev => ({ ...prev, allows_text_customization: e.target.checked }))}
                                        />
                                        <span>Permitir Personalización de Texto</span>
                                    </label>
                                </div>

                                <div className="admin-form-group">
                                    <label className="admin-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={formData.allows_logo_upload}
                                            onChange={(e) => setFormData(prev => ({ ...prev, allows_logo_upload: e.target.checked }))}
                                        />
                                        <span>Permitir Subida de Logo</span>
                                    </label>
                                </div>

                                <div className="admin-form-group">
                                    <label className="admin-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={formData.allows_pattern_upload}
                                            onChange={(e) => setFormData(prev => ({ ...prev, allows_pattern_upload: e.target.checked }))}
                                        />
                                        <span>Permitir Subida de Patrón</span>
                                    </label>
                                </div>

                                {formData.allows_text_customization && (
                                    <div className="admin-form-group">
                                        <Input
                                            label="Longitud Máxima de Texto"
                                            name="max_text_length"
                                            type="number"
                                            value={formData.max_text_length.toString()}
                                            setValue={(value) => setFormData(prev => ({ ...prev, max_text_length: parseInt(value) || 100 }))}
                                            placeholder="100"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Available Options */}
                        <div className="admin-form-section">
                            <h3 className="admin-section-title">Opciones Disponibles</h3>
                            <div className="admin-form-grid">
                                <div className="admin-form-group">
                                    <label className="admin-form-label">Materiales Disponibles</label>
                                    <div className="admin-multi-select">
                                        {materials.map(material => (
                                            <label key={material.id} className="admin-multi-option">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.available_materials.includes(material.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                available_materials: [...prev.available_materials, material.id]
                                                            }));
                                                        } else {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                available_materials: prev.available_materials.filter(id => id !== material.id)
                                                            }));
                                                        }
                                                    }}
                                                />
                                                <span>{material.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="admin-form-group">
                                    <label className="admin-form-label">Colores Disponibles</label>
                                    <div className="admin-multi-select">
                                        {colors.map(color => (
                                            <label key={color.id} className="admin-multi-option">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.available_colors.includes(color.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                available_colors: [...prev.available_colors, color.id]
                                                            }));
                                                        } else {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                available_colors: prev.available_colors.filter(id => id !== color.id)
                                                            }));
                                                        }
                                                    }}
                                                />
                                                <span>{color.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="admin-form-group">
                                    <label className="admin-form-label">Tamaños Disponibles</label>
                                    <div className="admin-multi-select">
                                        {sizes.map(size => (
                                            <label key={size.id} className="admin-multi-option">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.available_sizes.includes(size.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                available_sizes: [...prev.available_sizes, size.id]
                                                            }));
                                                        } else {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                available_sizes: prev.available_sizes.filter(id => id !== size.id)
                                                            }));
                                                        }
                                                    }}
                                                />
                                                <span>{size.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="admin-form-group">
                                    <label className="admin-form-label">Personalizaciones Disponibles</label>
                                    <div className="admin-multi-select">
                                        {customizations.map(customization => (
                                            <label key={customization.id} className="admin-multi-option">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.available_customizations.includes(customization.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                available_customizations: [...prev.available_customizations, customization.id]
                                                            }));
                                                        } else {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                available_customizations: prev.available_customizations.filter(id => id !== customization.id)
                                                            }));
                                                        }
                                                    }}
                                                />
                                                <span>{customization.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Bulk Pricing */}
                        <div className="admin-form-section">
                            <h3 className="admin-section-title">Precios por Volumen</h3>
                            <div className="admin-bulk-pricing">
                                {bulkPricing.map((tier, index) => (
                                    <div key={index} className="admin-pricing-tier">
                                        <div className="admin-form-grid">
                                            <div className="admin-form-group">
                                                <Input
                                                    name="min_quantity"
                                                    label="Cantidad Mínima"
                                                    type="number"
                                                    value={tier.min_quantity.toString()}
                                                    setValue={(value) => updateBulkPricingTier(index, 'min_quantity', parseInt(value) || 0)}
                                                    placeholder="0"
                                                />
                                            </div>
                                            <div className="admin-form-group">
                                                <Input
                                                    name="max_quantity"
                                                    label="Cantidad Máxima"
                                                    type="number"
                                                    value={tier.max_quantity?.toString() || ''}
                                                    setValue={(value) => updateBulkPricingTier(index, 'max_quantity', value ? parseInt(value) : undefined)}
                                                    placeholder="Sin límite"
                                                />
                                            </div>
                                            <div className="admin-form-group">
                                                <Input
                                                    name="discount_percentage"
                                                    label="Descuento (%)"
                                                    type="number"
                                                    step="0.01"
                                                    value={tier.discount_percentage.toString()}
                                                    setValue={(value) => updateBulkPricingTier(index, 'discount_percentage', parseFloat(value) || 0)}
                                                    placeholder="0.00"
                                                />
                                            </div>
                                            <div className="admin-form-group">
                                                <button
                                                    type="button"
                                                    onClick={() => removeBulkPricingTier(index)}
                                                    className="admin-remove-tier"
                                                >
                                                    <RiDeleteBinLine />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    onClick={addBulkPricingTier}
                                    className="admin-add-tier"
                                >
                                    <RiAddLine /> Agregar Nivel de Precio
                                </Button>
                            </div>
                        </div>

                        {/* SEO */}
                        <div className="admin-form-section">
                            <h3 className="admin-section-title">SEO</h3>
                            <div className="admin-form-grid">
                                <div className="admin-form-group admin-form-full">
                                    <Input
                                        label="Título Meta"
                                        name="meta_title"
                                        value={formData.meta_title}
                                        setValue={(value) => setFormData(prev => ({ ...prev, meta_title: value }))}
                                        placeholder="Título para motores de búsqueda"
                                    />
                                </div>

                                <div className="admin-form-group admin-form-full">
                                    <Input
                                        label="Descripción Meta"
                                        name="meta_description"
                                        value={formData.meta_description}
                                        setValue={(value) => setFormData(prev => ({ ...prev, meta_description: value }))}
                                        placeholder="Descripción para motores de búsqueda"
                                        rows={2}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="admin-modal-actions">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={isLoading}
                        >
                            {isLoading ? <Spinner size="sm" /> : null}
                            {isEditing ? 'Actualizar' : 'Crear'} Producto
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProductModal;
